import { BridgeComponent } from "@hotwired/hotwire-native-bridge"
import cable, { consumer, refreshCableIdentity } from "../../init/cable"

const ACTIONS = new Set(["start_scan", "stop_scan", "connect_device", "disconnect_device", "write", "write_bulk", "get_max_payload_size", "request_native_logs"])
const RESPONSES = new Set([
  "smartphone_ready",
  "did_start_scan",
  "did_reset_discovered_devices",
  "did_discover_device",
  "did_stop_scan",
  "did_connect",
  "did_disconnect",
  "did_write",
  "did_write_bulk",
  "write_bulk_progress",
  "did_notify",
  "did_get_max_payload_size",
  "process_error",
])

// Connects to data-controller="native--ble"
export default class extends BridgeComponent {
  static component = "ble"
  static targets = ["action", "permissionsWarning"]
  static values = { mode: String }

  connect() {
    super.connect()
    this.bleEnabled = false
    this.#changeStatus(false)
    this.#setKeepScreenAwake(true)
  }

  disconnect() {
    this.#setKeepScreenAwake(false)

    super.disconnect()

    // Abmelden auch ohne fertige Bridge (BLE noch nicht bereit): sonst bliebe das Abonnement bestehen und meldete
    // sich nach einem Reconnect des Cables auf einer anderen Seite wieder an.
    this.subscription?.unsubscribe()
    this.subscription = null
    if (!this.bleEnabled) return

    this.send("disconnect")
    this.bleEnabled = false
    this.#changeStatus(false)
  }

  async initializeBLE() {
    if (this.hasPermissionsWarningTarget) this.permissionsWarningTarget.classList.add("d-none")
    if (this.bleEnabled) return

    // Nach An- oder Abmelden per Turbo-Visit das Cable zuerst auf den aktuellen Benutzer umstellen (turbo:load kann
    // nach dem Verbinden dieses Controllers kommen).
    refreshCableIdentity()
    this.#cleanupSubscriptions()

    this.subscription = consumer.subscriptions.create(
      { channel: "BLEHandlerChannel", mode: this.modeValue },
      {
        connected: () => this.send("connect", {}, this.#receiveBridge.bind(this)),
        received: this.#receiveCable.bind(this),
        disconnected: () => {
          this.bleEnabled = false
          this.#changeStatus(false)
        },
      },
    )
  }

  permissionDenied() {
    if (this.hasPermissionsWarningTarget) this.permissionsWarningTarget.classList.remove("d-none")
  }

  // Leitet Nachrichten von AnyCable an die native Bridge weiter
  #receiveCable({ action, payload }) {
    if (action === "connect" || action === "disconnect") action = action + "_device"
    if (ACTIONS.has(action)) {
      this.send(action, { ...payload })
    } else console.error(`Unknown action: ${action}`, payload)
  }

  // Leitet Nachrichten von der Bridge an den Server weiter über AnyCable
  #receiveBridge({ data }) {
    if (data.action === "smartphone_ready") {
      if (data.error) {
        console.error("Failed to connect to BLE handler", data)
      } else {
        this.bleEnabled = true
        this.#changeStatus(true)
      }
    }

    if (RESPONSES.has(data.action)) this.subscription.perform(data.action, data)
    else console.error(`Unknown action: ${data.action}`, data)
  }

  // Im Batch-Modus laufen die Updates unbeaufsichtigt durch – der Bildschirm darf dabei nicht schlafen gehen.
  #setKeepScreenAwake(enabled) {
    if (this.modeValue !== "batch") return
    this.send("set_keep_screen_awake", { enabled })
  }

  #changeStatus(enabled) {
    this.actionTargets.forEach((element) => {
      element.disabled = !enabled
      if (!enabled) element.classList.add("disabled")
      else element.classList.remove("disabled")
    })
  }

  #cleanupSubscriptions() {
    cable.hub.channels.filter((c) => c.channelId === "BLEHandlerChannel").forEach((c) => c.subscription.unsubscribe())
  }
}
